import { COORDINATE_SYSTEM, Layer } from '@deck.gl/core';
import { ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import { getDateTimeFormatter } from '@fe/core';
import allPlanesPng from '../../img/all-planes-map-sdf.png';
import allPlanesJson from '../../img/all-planes-map.json';
import { iconPixelSize } from '../IconType';
import { DeckLayerCreator } from './deck-layer-creator';
import { DeckOverlayPlugin } from './deck-overlay-plugin';
import { FlightsIconLayer } from './layers/flight/flight-icon-layer';
import { FlightForwardFlight, FlightForwardProps } from './types';

export class DeckFlightForward extends DeckLayerCreator {
    private _flightForwardProps: FlightForwardProps = { show: false };
    private _forwardFlight: FlightForwardFlight | null = null;

    constructor(plugin: DeckOverlayPlugin) {
        super(plugin);
    }

    get flightForwardProps(): FlightForwardProps {
        return this._flightForwardProps;
    }

    set flightForwardProps(value: FlightForwardProps) {
        this._flightForwardProps = value;
    }

    get flight(): FlightForwardFlight | null {
        return this._forwardFlight;
    }

    set flight(value: FlightForwardFlight | null) {
        this._forwardFlight = value;
    }

    createLayers(): Layer[] {
        const { props } = this.plugin;

        if (!this.flightForwardProps?.show) {
            return [];
        }

        // Set real flight if no flight-forward
        const flight = this.flight;

        if (!flight) {
            return [];
        }

        const iconType = this.flightForwardProps.iconType;
        const formatter = getDateTimeFormatter();

        return [
            new ScatterplotLayer({
                id: 'flight-forward-marker',
                data: [flight],
                getPosition: (d) => d.position,
                getFillColor: [0, 20, 255, 60],
                getRadius: 20,
                radiusUnits: 'pixels',
                stroked: true,
                getLineColor: [255, 255, 0, 255],
                getLineWidth: 2,
                lineWidthUnits: 'pixels'
            }),
            new TextLayer({
                data: [flight],
                id: 'flight-forward-label',
                getPosition: (d) => d.position,
                background: true,
                getBackgroundColor: [0, 0, 0, 150],
                getPixelOffset: [25, -25],
                getColor: [255, 255, 255, 255],
                getSize: 12,
                getAngle: 0,
                billboard: true,
                coordinateSystem: COORDINATE_SYSTEM.LNGLAT,
                getText: () => `${flight.altitude?.toLocaleString('en-US') ?? '-'}ft ${formatter.formatTime(flight.time, 'hm')}`,
                backgroundPadding: [4, 4],
                getTextAnchor: 'start',
                fontFamily: 'helvetica',
                characterSet: 'auto',
                fontSettings: {
                    smoothing: 0.2
                }
            }),
            new FlightsIconLayer<FlightForwardFlight>({
                id: 'flight-forward-plane',
                data: [flight],
                visible: true,
                isGlobeView: this.useGlobeView,
                outlineColor: [0, 0, 0, 0.5],
                shadowColor: [0, 0, 0, 0.2],
                showShadow: false,
                billboard: false,
                sizeUnits: 'pixels',
                getPosition: (d) => d.position as [number, number],
                sizeScale: this.getIconScale(),
                getSize: iconPixelSize(iconType),
                getAngle: (d) => d.heading ?? 0,
                iconMapping: allPlanesJson,
                iconAtlas: allPlanesPng,
                sdfParams: this.plugin.planeSdfParams,
                getIcon: () => iconType.toString(),
                getIsHidden: 0,
                getColor: props?.flights.active.defaultFlightColor as [number, number, number, number],
                pickable: true,
                parameters: this.plugin.getRenderingParams({
                    cullMode: 'front'
                })
            })
        ];
    }
}
